/**
 * Builder model for the marketing-campaign "conditions" DSL.
 *
 * The DSL (evaluated by product/marketing potsy/config_parser.py) represents a
 * condition node as one of:
 *   - a string rule:   "<var> <op> <value>"   e.g. "status = ELIGIBLE"
 *   - a list:          implicit AND of child nodes
 *   - a dict:          { and: [...] } or { or: [...] }   (exactly one key)
 *
 * This module defines a structured, lossless representation of that grammar so
 * the UI can edit conditions visually and serialize them back to the exact YAML
 * the backend expects. See parse.ts / serialize.ts for the mappings.
 */

/** The comparison/logic operators supported by the DSL. */
export type ConditionOperator =
	| "="
	| "!="
	| ">"
	| "<"
	| ">="
	| "<="
	| "contains"
	| "not_contains"
	| "?" // exists (no value)
	| "!?"; // not-exists (no value)

export const ALL_OPERATORS: ConditionOperator[] = [
	"=",
	"!=",
	">",
	"<",
	">=",
	"<=",
	"contains",
	"not_contains",
	"?",
	"!?",
];

/** Operators that take no right-hand-side value. */
export const NO_VALUE_OPERATORS: ConditionOperator[] = ["?", "!?"];

export const operatorTakesValue = (op: ConditionOperator): boolean =>
	!NO_VALUE_OPERATORS.includes(op);

/** Time-offset units accepted on the right-hand side (canonical singular form). */
export type TimeUnit = "second" | "minute" | "hour" | "day" | "week" | "month" | "year";

export const TIME_UNITS: TimeUnit[] = ["second", "minute", "hour", "day", "week", "month", "year"];

/** The kinds of right-hand-side value the DSL understands. */
export type ConditionValueKind =
	| "literal"
	| "variable"
	| "datetime"
	| "nowRelative"
	| "varRelative";

/**
 * A right-hand-side value. Discriminated by `kind`:
 *   - literal:     a plain int/float/string compared as-is
 *   - variable:    another user_state field, written {{name}}
 *   - datetime:    an ISO date or datetime literal
 *   - nowRelative: NOW()  (offset 0)  or  NOW() ± N units
 *   - varRelative: <field> ± N units  (field must be a datetime)
 */
export type ConditionValue =
	| { kind: "literal"; value: string }
	| { kind: "variable"; name: string }
	| { kind: "datetime"; iso: string }
	| { kind: "nowRelative"; offset: number; unit: TimeUnit }
	| { kind: "varRelative"; varName: string; offset: number; unit: TimeUnit };

/** A single comparison, e.g. `status = ELIGIBLE`. */
export interface ConditionRule {
	id: string;
	fieldName: string;
	operator: ConditionOperator;
	/** Absent for the no-value operators (`?` / `!?`). */
	value?: ConditionValue;
}

/** An AND/OR group of rules and/or nested groups. */
export interface ConditionGroup {
	id: string;
	kind: "and" | "or";
	children: BuilderNode[];
}

/** Any node in the builder tree. `null` means "no conditions". */
export type BuilderNode = ConditionRule | ConditionGroup;

export const isGroup = (node: BuilderNode): node is ConditionGroup =>
	(node as ConditionGroup).kind === "and" || (node as ConditionGroup).kind === "or";

export const isRule = (node: BuilderNode): node is ConditionRule => !isGroup(node);

/** Short, collision-resistant id for React keys / tree diffing. */
export const newConditionId = (): string => `c_${Math.random().toString(36).slice(2, 10)}`;

/* ---------------------------------------------------------------------------
 * Factories
 * ------------------------------------------------------------------------- */

export const emptyRule = (fieldName = ""): ConditionRule => ({
	id: newConditionId(),
	fieldName,
	operator: "=",
	value: { kind: "literal", value: "" },
});

export const emptyGroup = (kind: "and" | "or" = "and"): ConditionGroup => ({
	id: newConditionId(),
	kind,
	children: [],
});

/* ---------------------------------------------------------------------------
 * Field-type → allowed operators / default value kind
 *
 * Field types come from useTemplateVariables(), which serves the raw PostgreSQL
 * `information_schema.columns.data_type` of each user_state_tmp column — e.g.
 * "timestamp without time zone", "time without time zone", "date", "boolean",
 * "text", "numeric", "bigint", "ARRAY". So we match by SUBSTRING, never exact
 * equality. Free-typed fields (unknown type) allow every operator.
 * ------------------------------------------------------------------------- */

const TEXT_OPS: ConditionOperator[] = ["=", "!=", "contains", "not_contains", "?", "!?"];
const NUMERIC_OPS: ConditionOperator[] = ["=", "!=", ">", "<", ">=", "<=", "?", "!?"];
const BOOLEAN_OPS: ConditionOperator[] = ["=", "!=", "?", "!?"];

const includesAny = (haystack: string, needles: string[]): boolean =>
	needles.some((needle) => haystack.includes(needle));

/** Whether a field type is date/time-like (timestamp, date, or time columns). */
export const isDateLikeType = (fieldType?: string): boolean =>
	includesAny((fieldType ?? "").toUpperCase(), ["TIMESTAMP", "DATETIME", "DATE", "TIME"]);

export const isBooleanType = (fieldType?: string): boolean =>
	(fieldType ?? "").toUpperCase().includes("BOOL");

const isNumericType = (type: string): boolean =>
	includesAny(type, ["INT", "NUMERIC", "DECIMAL", "FLOAT", "DOUBLE", "REAL", "MONEY", "SERIAL"]);

export const operatorsForFieldType = (fieldType?: string): ConditionOperator[] => {
	const type = (fieldType ?? "").toUpperCase();
	if (!type) return ALL_OPERATORS;
	if (isBooleanType(type)) return BOOLEAN_OPS;
	// Dates and numbers both support ordering comparisons.
	if (isDateLikeType(type) || isNumericType(type)) return NUMERIC_OPS;
	if (includesAny(type, ["TEXT", "CHAR", "ARRAY", "[]", "JSON", "UUID"])) return TEXT_OPS;
	return ALL_OPERATORS;
};
